import React from 'react';

const ASPECT_RATIO = '75%'; // 4:3 aspect ratio placeholder

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Prevent right-click context menu (download option)
const preventContextMenu = (e) => {
  e.preventDefault();
};

// Prevent drag-to-save operations
const preventDrag = (e) => {
  e.preventDefault();
};

/**
 * Responsive grid of media thumbnails.
 *
 * Props:
 *   items      – array of { id, name, url, media_type, size_bytes }
 *   onItemClick – (item, index) => void
 *   loading    – bool
 */
export default function MediaGrid({ items = [], onItemClick, loading = false }) {
  if (loading) {
    return (
      <div className="media-grid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="media-grid-skeleton" aria-hidden="true">
            <div className="skeleton" style={{ paddingBottom: ASPECT_RATIO }} />
          </div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="media-grid-empty">
        <span style={{ fontSize: '2.5rem' }}>📷</span>
        <p>No media found.</p>
      </div>
    );
  }

  return (
    <div className="media-grid" role="list" aria-label="Media gallery">
      {items.map((item, index) => {
        const isVideo = item.media_type === 'video';
        const thumbSrc = item.thumbnail_url;
        return (
          <button
            key={item.id || index}
            type="button"
            className="media-grid-item"
            onClick={() => onItemClick?.(item, index)}
            aria-label={`View ${item.name || 'media item'} ${isVideo ? '(video)' : '(photo)'}`}
            role="listitem"
          >
            <div className="media-grid-thumb" style={{ position: 'relative' }}>
              {!isVideo ? (
                <img
                  src={item.url}
                  alt={item.name || 'Gallery item'}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  onContextMenu={preventContextMenu}
                  onDragStart={preventDrag}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => {
                    e.target.parentElement.style.background = '#e9ecef';
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <>
                  {thumbSrc ? (
                    <img
                      src={thumbSrc}
                      alt={item.name || 'Video thumbnail'}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                      onContextMenu={preventContextMenu}
                      onDragStart={preventDrag}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.background = '#e9ecef';
                      }}
                    />
                  ) : (
                    <div className="media-grid-play" aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
                      <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  )}
                  <div
                    className="media-grid-play"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '48px',
                      height: '48px',
                      background: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="white" style={{ marginLeft: '2px' }}>
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </>
              )}
            </div>
            <div className="media-grid-caption">
              <span className="media-grid-name">{item.name}</span>
              {item.size_bytes && (
                <span className="media-grid-size">{formatBytes(item.size_bytes)}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
